/**
 * RPD Report - the request shapes, the pairing-gate vocabulary and the save-dialog states.
 *
 * The surface these feed is client-reviewed (see AI_Docs/RPD/UI_Design). The gate wording and
 * the four causes are lifted verbatim from report-input.html so the rendered bands match the
 * reviewed mockups; the layout itself is frozen and must not be reshaped here.
 *
 * Every route below is one of the 13 in the approved endpoint plan. Tenant, user and message
 * scoping are all resolved server side, so nothing in this file sends a tenant or an owner.
 *
 * Wire casing: this API has no camelCase contract resolver (Startup.cs:38 is commented out), so
 * both the request bodies and the response payloads are PascalCase. Requests are built in that
 * casing, and the `to*` normalisers at the foot of this file translate a payload into the
 * camelCase shape the components consume - keeping the wire casing in one place.
 */

export const RPD_PATHS = {
  aliasOptions: 'api/Rpd/AliasOptions',
  resolve: 'api/Rpd/Resolve',
  options: 'api/Rpd/Options',
  generate: 'api/Rpd/Generate',
  savedQueries: 'api/Rpd/SavedQueries',
  savedQuery: (id) => `api/Rpd/SavedQueries/${encodeURIComponent(id)}`,
  /* Existing route, reused as-is for the exclusion picker's catalogue. */
  analyteList: 'api/Analyte/List/',
}

/* ------------------------------------------------------------------ filters */

/** The entity defaults, mirrored from `RpdCalculationOptions`. The server re-validates them. */
export const RPD_DEFAULT_ACCEPTANCE_THRESHOLD = 20
export const RPD_DEFAULT_DETECTION_LIMIT_MULTIPLIER = 5

/** The per-query thresholds, as the UI holds them. */
export function emptyThresholds() {
  return {
    acceptanceThreshold: RPD_DEFAULT_ACCEPTANCE_THRESHOLD,
    detectionLimitMultiplier: RPD_DEFAULT_DETECTION_LIMIT_MULTIPLIER,
  }
}

/**
 * The thresholds in wire casing. A blank or non-numeric field falls back to the default rather
 * than sending NaN, which would be rejected as a 400 for what is really an empty input.
 */
export function toThresholds(model) {
  const numeric = (value, fallback) => {
    if (value === '' || value === null || value === undefined) return fallback
    const parsed = Number(value)
    return isNaN(parsed) ? fallback : parsed
  }

  const source = model || {}

  return {
    AcceptanceThreshold: numeric(source.acceptanceThreshold, RPD_DEFAULT_ACCEPTANCE_THRESHOLD),
    DetectionLimitMultiplier: numeric(source.detectionLimitMultiplier, RPD_DEFAULT_DETECTION_LIMIT_MULTIPLIER),
  }
}

/**
 * One side's filter set, in wire casing. Empty arrays are the only representation of "no
 * filter" - the server has no null form, so clearing a picker must produce [] and never null.
 */
export function emptyFilterSet() {
  return {
    LocationId: null,
    From: null,
    To: null,
    TimeFrom: null,
    TimeTo: null,
    AliasLabels: [],
    MediaTypes: [],
    Search: '',
  }
}

export function emptyPairing() {
  return {
    Primary: emptyFilterSet(),
    Duplicate: emptyFilterSet(),
  }
}

/** True when a side carries no filter at all, which can only ever match everything. */
export function isEmptyFilterSet(filter) {
  if (!filter) return true
  return !filter.LocationId &&
    !filter.From &&
    !filter.To &&
    !filter.TimeFrom &&
    !filter.TimeTo &&
    !(filter.AliasLabels && filter.AliasLabels.length) &&
    !(filter.MediaTypes && filter.MediaTypes.length) &&
    !(filter.Search && filter.Search.trim())
}

/** Normalises a side into the shape the routes expect, without sending undefined. */
export function normaliseFilterSet(filter) {
  const source = filter || {}
  return {
    LocationId: source.LocationId || null,
    From: source.From || null,
    To: source.To || null,
    TimeFrom: source.TimeFrom || null,
    TimeTo: source.TimeTo || null,
    AliasLabels: [...(source.AliasLabels || [])],
    MediaTypes: [...(source.MediaTypes || [])],
    Search: source.Search || '',
  }
}

export function buildResolveRequest(pairing) {
  return {
    Primary: normaliseFilterSet(pairing && pairing.Primary),
    Duplicate: normaliseFilterSet(pairing && pairing.Duplicate),
  }
}

export function buildGenerateRequest(pairing, thresholds, excludedAnalyteIds) {
  return {
    Primary: normaliseFilterSet(pairing && pairing.Primary),
    Duplicate: normaliseFilterSet(pairing && pairing.Duplicate),
    Thresholds: toThresholds(thresholds),
    ExcludedAnalyteIds: [...(excludedAnalyteIds || [])],
  }
}

/**
 * The design's identity check: both sides describing one monitoring location *and* one alias set
 * is one sample filtered twice rather than a pair. The server owns the verdict; the UI only
 * reports it, and uses it to explain the block.
 */
export function identityComparison(primary, duplicate, locationLabels, aliasLabels) {
  const p = normaliseFilterSet(primary)
  const d = normaliseFilterSet(duplicate)

  const locationSame = !!p.LocationId && p.LocationId === d.LocationId
  const aliasesSame = p.AliasLabels.length > 0 &&
    canonicalList(p.AliasLabels) === canonicalList(d.AliasLabels)

  return {
    locationSame,
    aliasesSame,
    collides: locationSame && aliasesSame,
    locationPrimary: labelFor(locationLabels, p.LocationId),
    locationDuplicate: labelFor(locationLabels, d.LocationId),
    aliasPrimary: describeAliases(p.AliasLabels, aliasLabels),
    aliasDuplicate: describeAliases(d.AliasLabels, aliasLabels),
  }
}

function labelFor(labels, id) {
  if (!id) return 'Any'
  return (labels && labels[id]) || String(id)
}

function canonicalList(values) {
  return [...(values || [])].slice().sort().join('|')
}

function describeAliases(ids, aliasLabels) {
  if (!ids || !ids.length) return 'Any'
  return ids.map((id) => (aliasLabels && aliasLabels[id]) || id).join(', ')
}

/* ------------------------------------------------------------------ the gate */

const GATE_READY = {
  kind: 'ready',
  icon: 'check_circle',
  title: 'Both sides resolved to exactly one sample',
  detail: 'Workbook generation is enabled. A field duplicate is a distinct sample, so the two identifiers are not expected to match.',
}

export const RPD_GATE_IDLE = {
  kind: 'idle',
  icon: 'info',
  title: 'Confirm the filter to check the gate',
  detail: 'The samples a side selects are fetched when you confirm, not on every change. Each side is filtered independently and must resolve to exactly one sample before a workbook can be generated.',
}

/**
 * The same idle kind, worded for filters that were confirmed and then edited. The verdict on screen
 * is the previous filters', so it is not shown as current.
 */
export const RPD_GATE_STALE = {
  kind: 'idle',
  icon: 'info',
  title: 'Filters changed since the last check',
  detail: 'The verdict below is from the filters as they were when they were confirmed. Confirm the filter to fetch the samples these filters select.',
}

/**
 * The gate kinds the server can report, mirroring the prototype's causes. `sameSample` covers
 * both degenerate shapes the server reports under one kind: the two sides resolving to one
 * ClientSampleID, and the two filter sets describing one location and one alias set.
 */
const GATE_BY_KIND = {
  ready: GATE_READY,

  ambiguousPrimary: {
    kind: 'blocked',
    icon: 'error',
    title: 'Primary resolved to more than one sample. Narrow the filters.',
    detail: 'Too many samples matched. Shorten the date range, or remove a QA/QC alias or media type value to narrow the primary side.',
  },

  ambiguousDuplicate: {
    kind: 'blocked',
    icon: 'error',
    title: 'Duplicate resolved to more than one sample. Narrow the filters.',
    detail: 'Too many samples matched. Shorten the date range, or remove a QA/QC alias or media type value to narrow the duplicate side.',
  },

  noMatchPrimary: {
    kind: 'blocked',
    icon: 'error',
    title: 'Primary resolved to no samples. Relax the filters.',
    detail: 'Nothing matched. Widen the date range, or add a QA/QC alias or media type on the primary side.',
  },

  noMatchDuplicate: {
    kind: 'blocked',
    icon: 'error',
    title: 'Duplicate resolved to no samples. Relax the filters.',
    detail: 'Nothing matched. Widen the date range, or add a QA/QC alias or media type on the duplicate side.',
  },

  sameSample: {
    kind: 'blocked',
    icon: 'content_copy',
    title: 'Both sides resolve to the same sample',
    detail: 'Primary and Duplicate share a monitoring location and a QA/QC alias set, so they select one sample rather than a pair. Give each side its own alias - Field Sample for the primary, Field Duplicate for the duplicate - or point them at different locations.',
  },
}

/**
 * The band model for a server gate kind. An unknown or absent kind reads as idle, so a payload
 * this build does not recognise blocks generation instead of silently opening it.
 */
export function gateBand(gateKind) {
  return GATE_BY_KIND[gateKind] || RPD_GATE_IDLE
}

/** Generation is unblocked only by an explicit ready. */
export function isGateOpen(gateKind) {
  return gateKind === 'ready'
}

/* ------------------------------------------------------------------ resolution */

/** One side's readout. Mirrors the prototype's three states: none, one, many. */
export function resolutionState(sampleIds) {
  const ids = sampleIds || []
  if (ids.length === 1) {
    return { cls: 'resolution--ok', icon: 'check_circle', word: '1 sample' }
  }
  if (ids.length === 0) {
    return { cls: 'resolution--bad', icon: 'error_outline', word: 'No samples' }
  }
  return { cls: 'resolution--warn', icon: 'warning', word: `${ids.length} samples` }
}

/** The readout's meta line, which explains why a side is not usable. */
export function resolutionMeta(sampleIds, isTheOtherSidesSample) {
  const ids = sampleIds || []
  if (ids.length === 0) return 'Nothing matched these filters.'
  if (ids.length > 1) return 'Each side must resolve to exactly one sample.'
  if (isTheOtherSidesSample) return 'This is the sample the other side resolved to.'
  return 'ClientSampleID'
}

/* ------------------------------------------------------------------ submission */

/**
 * The bar is single-state because the caller no longer tracks the job: submitting hands the work to the
 * message centre, which is the same contract the discrete-data-query report uses. There is no poll, no
 * download-from-the-bar and no terminal state to render.
 */
export const RPD_EXPORT_NOTE = 'Generating runs the full calculation and can take a moment. Results are written to the workbook, not shown here.'
/* ------------------------------------------------------------------ saved queries */

/**
 * The three name-collision states the save dialog renders. The drawer derives them from the list
 * it already holds, so opening the dialog costs no round trip; the server's 409 on the write is
 * the race fallback for a name taken between the list load and the save.
 */
export const RPD_SAVE_STATES = {
  create: {
    key: 'create',
    cls: 'dialog__banner--new',
    icon: 'check_circle',
    title: 'Saving as a new query',
    detail: 'No saved query uses this name.',
    button: 'btn btn--success',
    label: 'Save',
  },
  update: {
    key: 'update',
    cls: 'dialog__banner--update',
    icon: 'info',
    title: 'Updating an existing query you own',
    detail: 'The filters, criteria, and exclusions below will replace the saved ones.',
    button: 'btn btn--primary',
    label: 'Update',
  },
  overwrite: {
    key: 'overwrite',
    cls: 'dialog__banner--overwrite',
    icon: 'warning',
    title: 'Overwriting a query shared by another user',
    detail: 'Another user owns a query with this name. Saving replaces it for everyone.',
    button: 'btn btn--warning',
    label: 'Overwrite',
  },
}

/** The saved query a typed name matches, or undefined. Matching is case-insensitive. */
export function savedQueryFor(name, savedQueries) {
  const wanted = (name || '').trim().toLowerCase()
  if (!wanted) return undefined
  return (savedQueries || []).find(
    (query) => (query.Name || '').trim().toLowerCase() === wanted,
  )
}

/** Which save state a typed name falls into, derived from the drawer's own list. */
export function saveState(name, savedQueries, currentUserId) {
  const existing = savedQueryFor(name, savedQueries)
  if (!existing) return RPD_SAVE_STATES.create

  /* The owner arrives as a Guid string on the wire and as a claim, so the two can differ in case. */
  const mine = !!currentUserId &&
    String(existing.OwnerUserId).toLowerCase() === String(currentUserId).toLowerCase()

  return mine ? RPD_SAVE_STATES.update : RPD_SAVE_STATES.overwrite
}

/** The dialog's overwrite detail names the owner, so the warning is actionable. */
export function saveStateDetail(state, existing) {
  if (state !== RPD_SAVE_STATES.overwrite || !existing) return state.detail
  const owner = existing.OwnerDisplayName || 'Another user'
  return `${owner} owns a query with this name. Saving replaces it for everyone.`
}

/** A saved query's configuration as a pairing, so the drawer can reopen it. */
export function pairingFromConfiguration(configuration) {
  if (!configuration) return emptyPairing()

  const side = (saved) => ({
    ...emptyFilterSet(),
    LocationId: saved && saved.LocationId ? saved.LocationId : null,
    From: saved && saved.From ? saved.From : null,
    To: saved && saved.To ? saved.To : null,
    TimeFrom: saved && saved.TimeFrom ? saved.TimeFrom : null,
    TimeTo: saved && saved.TimeTo ? saved.TimeTo : null,
    AliasLabels: [...((saved && saved.AliasLabels) || [])],
    MediaTypes: [...((saved && saved.MediaTypes) || [])],
    Search: (saved && saved.Search) || '',
  })

  return {
    Primary: side(configuration.Primary),
    Duplicate: side(configuration.Duplicate),
  }
}

/** A saved query's configuration as the UI's threshold model, so the drawer can reopen it. */
export function thresholdsFromConfiguration(configuration) {
  if (!configuration) return emptyThresholds()

  const fallback = (value, defaultValue) => (
    value === undefined || value === null ? defaultValue : value
  )

  return {
    acceptanceThreshold: fallback(
      configuration.AcceptanceThreshold,
      RPD_DEFAULT_ACCEPTANCE_THRESHOLD,
    ),
    detectionLimitMultiplier: fallback(
      configuration.DetectionLimitMultiplier,
      RPD_DEFAULT_DETECTION_LIMIT_MULTIPLIER,
    ),
  }
}

/** A saved query's per-query exclusions. These sit on top of the tenant list, never replace it. */
export function excludedAnalyteIdsFromConfiguration(configuration) {
  if (!configuration) return []
  return [...(configuration.ExcludedAnalyteIds || [])]
}

/** The pairing, thresholds and exclusions as the stored configuration. */
export function configurationFromPairing(pairing, thresholds, excludedAnalyteIds, includeMedia) {
  const p = normaliseFilterSet(pairing && pairing.Primary)
  const d = normaliseFilterSet(pairing && pairing.Duplicate)
  const t = toThresholds(thresholds)

  return {
    Primary: p,
    Duplicate: d,
    AcceptanceThreshold: t.AcceptanceThreshold,
    DetectionLimitMultiplier: t.DetectionLimitMultiplier,
    ExcludedAnalyteIds: [...(excludedAnalyteIds || [])],
    IncludeMedia: !!includeMedia,
  }
}

/* ------------------------------------------------- wire payload -> view models */

/** `GET api/Rpd/AliasOptions` -> the two pickers' options, plus id -> label maps. */
export function toAliasOptions(payload) {
  const aliases = (payload && payload.Aliases) || []
  const mediaTypes = (payload && payload.MediaTypes) || []

  const aliasLabels = {}
  aliases.forEach((alias) => { aliasLabels[alias.Id] = alias.Label })

  const mediaLabels = {}
  mediaTypes.forEach((media) => { mediaLabels[media.Id] = media.Label })

  return {
    aliases: aliases.map((alias) => ({
      id: alias.Id,
      label: alias.Label,
      isQaQc: !!alias.IsQaQc,
      isFieldData: !!alias.IsFieldData,
    })),
    mediaTypes: mediaTypes.map((media) => ({ id: media.Id, label: media.Label })),
    aliasLabels,
    mediaLabels,
  }
}

/**
 * `POST api/Rpd/Options` -> the tenant layer. The response carries exclusions as AnalyteKey
 * objects (`{ AnalyteId, Fraction }`), while Generate takes plain ids, so the ids are projected
 * out here and the keys are kept alongside for the picker's labels.
 */
export function toTenantOptions(payload) {
  const excluded = (payload && payload.ExcludedAnalytes) || []
  const keys = excluded.map((key) => ({
    analyteId: key.AnalyteId,
    fraction: key.Fraction || '-',
  }))

  return {
    acceptanceThreshold: payload ? payload.AcceptanceThreshold : null,
    detectionLimitMultiplier: payload ? payload.DetectionLimitMultiplier : null,
    excludedAnalyteKeys: keys,
    excludedAnalyteIds: keys.map((key) => key.analyteId),
  }
}

/**
 * The resolve gate. A ready response is a 200; the two blocked causes arrive as errors, so this
 * takes the payload of whichever status the caller caught. `RpdAmbiguityResponse` carries no
 * GateKind, so the side is derived from the match counts - primary first, matching the server's
 * own classification order.
 */
export function toResolveOutcome(payload, httpStatus) {
  const primary = (payload && payload.PrimarySampleIds) || []
  const duplicate = (payload && payload.DuplicateSampleIds) || []

  if (httpStatus === 409) {
    return {
      gateKind: primary.length > 1 ? 'ambiguousPrimary' : 'ambiguousDuplicate',
      primarySampleIds: primary,
      duplicateSampleIds: duplicate,
      message: payload ? payload.Message : null,
      isUnderConstrained: !!(payload && payload.IsUnderConstrained),
    }
  }

  if (httpStatus === 422) {
    return {
      gateKind: (payload && payload.GateKind) || 'noMatchPrimary',
      primarySampleIds: primary,
      duplicateSampleIds: duplicate,
      message: payload ? payload.Message : null,
      isUnderConstrained: false,
    }
  }

  return {
    gateKind: (payload && payload.GateKind) || 'idle',
    primarySampleIds: primary,
    duplicateSampleIds: duplicate,
    message: null,
    isUnderConstrained: false,
  }
}

/**
 * `POST api/Rpd/Generate` -> the id of the message the work was handed to. That id is for logging and
 * support only: the caller does not use it to track the job, because the message centre already renders
 * the message and its details.
 */
export function toJobAccepted(payload) {
  return {
    messageId: payload ? payload.MessageId : null,
  }
}

/** `GET api/Rpd/SavedQueries` -> the drawer's list, in wire casing for the save-state helpers. */
export function toSavedQueries(payload) {
  return (payload || []).map((query) => ({
    Id: query.Id,
    Name: query.Name,
    OwnerUserId: query.OwnerUserId,
    OwnerDisplayName: query.OwnerDisplayName,
    IsShared: !!query.IsShared,
    Description: query.Description,
    UpdatedAt: query.UpdatedAt,
    CanOverwrite: !!query.CanOverwrite,
    CanDelete: !!query.CanDelete,
  }))
}

/* --------------------------------------------------- the exclusion picker */

/**
 * Mirrors the server's `AnalyteFraction.Extract`, which anchors the match to the end of the name
 * so "Dissolved Oxygen" and "Total Dissolved Solids" resolve to no fraction. The server stays
 * authoritative - Generate takes ids only and re-derives the fraction - so this exists purely to
 * label the picker's rows, which is why it is not sent anywhere.
 */
export function analyteFraction(analyteName) {
  const name = (analyteName || '').trim()

  if (/-Total$/i.test(name)) return 'Total'
  if (/-Dissolved$/i.test(name)) return 'Dissolved'
  return '-'
}

/**
 * `GET api/Analyte/List` -> the tenant's analyte catalogue for the exclusion picker. This is an
 * existing route, reused as-is; the analyte table is already tenant-scoped server side.
 */
export function toAnalyteCatalogue(payload) {
  return (payload || []).map((analyte) => {
    const name = analyte.DisplayName || analyte.AnalyteName || ''

    return {
      id: analyte.Id,
      name,
      /* The native name carries the fraction suffix; the display name does not. */
      fraction: analyteFraction(analyte.AnalyteName),
    }
  })
}

/*
 * A discrete station projected into the two location selects. Only the location's name is shown:
 * the identifier, category and type would be extra text beside a value the reviewer picks by name.
 */
export function toLocationOption(station) {
  const name = station.PrimaryName || station.LocationName || station.LocationIdentifier || ''

  return {
    id: station.Id,
    label: name,
  }
}
